import { initializeApp, getApps, type FirebaseApp, type FirebaseOptions } from "firebase/app";
import { getAnalytics, isSupported as analyticsIsSupported, type Analytics } from "firebase/analytics";
import { getDatabase, type Database } from "firebase/database";
import { getFunctions, type Functions } from "firebase/functions";

const DEFAULT_DATABASE_URL = "https://satoshi-c-nakamoto-default-rtdb.firebaseio.com";
const LOCAL_DEV_CONFIG: FirebaseOptions = {
  apiKey: "AIzaSyBH9hUInBKa1d2VC7GZz_5pYLfkAk4SBz8",
  authDomain: "satoshi-c-nakamoto.firebaseapp.com",
  projectId: "satoshi-c-nakamoto",
  storageBucket: "satoshi-c-nakamoto.firebasestorage.app",
  messagingSenderId: "280436233666",
  appId: "1:280436233666:web:304a8482c1d1aaac74f25b",
  measurementId: "G-2JEPRBSNMD",
  databaseURL: DEFAULT_DATABASE_URL
};

export let app: FirebaseApp;
export let database: Database;
export let functions: Functions;
export let analytics: Analytics | null = null;

function env(key: string): string | undefined {
  return (import.meta as any)?.env?.[key] as string | undefined;
}

function getConfigFromEnv(): FirebaseOptions | null {
  const apiKey = env("VITE_FIREBASE_API_KEY");
  const authDomain = env("VITE_FIREBASE_AUTH_DOMAIN");
  const projectId = env("VITE_FIREBASE_PROJECT_ID");
  const storageBucket = env("VITE_FIREBASE_STORAGE_BUCKET");
  const messagingSenderId = env("VITE_FIREBASE_MESSAGING_SENDER_ID");
  const appId = env("VITE_FIREBASE_APP_ID");

  if (!apiKey || !authDomain || !projectId || !storageBucket || !messagingSenderId || !appId) {
    return null;
  }

  return {
    apiKey,
    authDomain,
    projectId,
    storageBucket,
    messagingSenderId,
    appId,
    measurementId: env("VITE_FIREBASE_MEASUREMENT_ID"),
    databaseURL: env("VITE_FIREBASE_DATABASE_URL") || DEFAULT_DATABASE_URL
  };
}

async function getConfigFromFirebaseHosting(): Promise<FirebaseOptions> {
  const res = await fetch("/__/firebase/init.json", { cache: "no-store" });
  if (!res.ok) {
    throw new Error(
      "Firebase config missing. Set VITE_FIREBASE_* env vars at build time, or host on Firebase Hosting so /__/firebase/init.json is available."
    );
  }

  const cfg = (await res.json()) as Record<string, unknown>;
  return {
    apiKey: String(cfg.apiKey || ""),
    authDomain: String(cfg.authDomain || ""),
    projectId: String(cfg.projectId || ""),
    storageBucket: String(cfg.storageBucket || ""),
    messagingSenderId: String(cfg.messagingSenderId || ""),
    appId: String(cfg.appId || ""),
    measurementId: cfg.measurementId ? String(cfg.measurementId) : undefined,
    databaseURL: env("VITE_FIREBASE_DATABASE_URL") || (cfg.databaseURL ? String(cfg.databaseURL) : DEFAULT_DATABASE_URL)
  };
}

export async function initFirebase(): Promise<void> {
  if (getApps().length > 0) {
    // Already initialized elsewhere
    app = getApps()[0]!;
    database = getDatabase(app);
    functions = getFunctions(app);
    return;
  }

  let config = getConfigFromEnv();
  if (!config) {
    try {
      config = await getConfigFromFirebaseHosting();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn("[Firebase] Using local development config; cloud services are unavailable.", error);
        config = LOCAL_DEV_CONFIG;
      } else {
        throw error;
      }
    }
  }

  // Minimal sanity check
  if (!config.apiKey || !config.projectId || !config.appId) {
    throw new Error("Invalid Firebase config (missing apiKey/projectId/appId)." );
  }

  app = initializeApp(config);
  database = getDatabase(app);
  functions = getFunctions(app);

  // Analytics is optional and its remote setup rejects with a placeholder key
  // during local development, so keep it out of the dev boot path.
  if (typeof window !== "undefined" && !import.meta.env.DEV) {
    try {
      if (await analyticsIsSupported()) {
        analytics = getAnalytics(app);
      }
    } catch {
      analytics = null;
    }
  }
}
